import React, { useState, useCallback, useEffect } from 'react'
import styled from '@emotion/styled'
import { colors } from '../../../../constants'
import {
  Account,
  GOClientLocation,
  GOClientSettings,
  GOSchedMenuChef,
  GOScheduleMenu,
  DateMenuTemplate,
  Option,
  Cuisine,
  ShowInfoModalReq,
} from '@types'
import { FiInfo } from 'react-icons/fi'
import GroupOrderMenusFinder from './MenusFinder/GroupOrderMenusFinder'
import { SearchGOMenusResponse } from '@coordinators/groupOrders/menus'
import {
  CreateDateMenuTemplateReq,
  UpdateDateMenuTemplateReq,
} from '@coordinators/groupOrders/dateMenuTemplates'
import MenuSuggestionsMap from './Map/MenuSuggestionsMap'
import { Filters } from './MenusFinder/useGOMenuSearch'
import { SearchClientSettingsParams } from './ClientLocationSelector'

interface GOSchedulerPlannerProps {
  hq: number
  userId: string
  userEmail: string
  loadAccounts: (val: { search: string }) => Promise<Account[]>
  loadChefs: (val: { ids: string }) => Promise<GOSchedMenuChef[]>
  loadCuisines: () => Promise<any[]>
  searchClientSettings: (
    params: SearchClientSettingsParams,
  ) => Promise<GOClientSettings[]>
  searchGOMenus: (val: any) => Promise<SearchGOMenusResponse | null>
  showInfoModal: (req: ShowInfoModalReq) => void
  closeInfoModal: () => void
  displayFailureMessage: (mssg: string) => void
  displayInfoMessage: (mssg: string) => void
  displayConfirmationModal: ({
    title,
    text,
  }: {
    title: string
    text: string
  }) => Promise<boolean>
  getDateMenuTemplate: (id: string) => Promise<DateMenuTemplate | null>
  createDateMenuTemplate: (
    req: CreateDateMenuTemplateReq,
  ) => Promise<DateMenuTemplate | null>
  updateDateMenuTemplate: (
    req: UpdateDateMenuTemplateReq,
  ) => Promise<DateMenuTemplate | null>
  searchDateMenuTemplates: (val: any) => Promise<DateMenuTemplate[] | null>
  deleteDateMenuTemplates: (id: string) => Promise<boolean>
}

enum PlannerView {
  MenuFinder = 'MenuFinder',
  MenuSuggestions = 'MenuSuggestions',
}

const GroupOrderSchedulePlanner = ({
  hq,
  userId,
  userEmail,
  searchClientSettings,
  searchGOMenus,
  loadAccounts,
  loadChefs,
  loadCuisines,
  displayFailureMessage,
  displayInfoMessage,
  displayConfirmationModal,
  showInfoModal,
  closeInfoModal,
  createDateMenuTemplate,
  updateDateMenuTemplate,
  searchDateMenuTemplates,
  deleteDateMenuTemplates,
}: GOSchedulerPlannerProps) => {
  // Parent shared state
  const [currentView, setCurrentView] = useState<PlannerView>(
    PlannerView.MenuFinder,
  )
  const [cuisineOptions, setCuisineOptions] = useState<Option[]>([])

  // Account and client location state
  const [account, setAccount] = useState<Account | null>(null)
  const [selectedClientSetting, setSelectedClientSetting] =
    useState<GOClientSettings | null>(null)
  const [selectedClientLocation, setSelectedClientLocation] =
    useState<GOClientLocation | null>(null)
  const [withinBudget, setWithinBudget] = useState<boolean | null>(null)

  const onSelectClientLocation = (
    account: Account,
    setting: GOClientSettings,
    location: GOClientLocation,
  ) => {
    setAccount(account)
    setSelectedClientSetting(setting)
    setSelectedClientLocation(location)
    setWithinBudget(false)
  }

  // Menu finder state
  const [availMenus, setAvailMenus] = useState<GOScheduleMenu[]>([])
  const [savedFilters, saveFilters] = useState<Filters | null>(null)

  // Load cuisines on mount
  const onLoadCuisines = useCallback(async () => {
    const cuisineTags = await loadCuisines()
    const cuisines = cuisineTags.map((c: Cuisine) => {
      return {
        label: c.name,
        value: c.name,
      }
    })
    setCuisineOptions(cuisines)
  }, [loadCuisines])

  useEffect(() => {
    onLoadCuisines()
  }, [onLoadCuisines])

  const handleShowMenuFinderInfo = () => {
    showInfoModal({
      title: 'Menu Search Information',
      content: <MenuFinderInfo />,
    })
  }

  const handleShowMapInfo = () => {
    showInfoModal({
      title: 'Map & Build Date Menus Information',
      content: <MapInfo />,
    })
  }

  return (
    <GOSchedulePlannerContainer>
      {account && selectedClientLocation && selectedClientSetting && (
        <NavContainer>
          <ClientSelectionSection>
            <NavSectionHeader>
              <NavSectionTitle>Client Selection</NavSectionTitle>
              <ClientSelectedInfo>
                <NavLabel>Selected:</NavLabel>
                <strong>
                  {account.name} | {selectedClientSetting.name} |{' '}
                  {selectedClientLocation.name}
                </strong>
              </ClientSelectedInfo>
            </NavSectionHeader>
          </ClientSelectionSection>
        </NavContainer>
      )}

      <WorkflowContainer>
        <WorkflowSection
          active={currentView === PlannerView.MenuFinder}
          onClick={() => setCurrentView(PlannerView.MenuFinder)}
        >
          <NavSectionContent>
            <NavTitleRow>
              <NavTitle>1. Menu Search</NavTitle>
              <InfoIconWrapper
                onClick={(e) => {
                  e.stopPropagation()
                  handleShowMenuFinderInfo()
                }}
              >
                <StyledInfoIcon size={22} />
              </InfoIconWrapper>
            </NavTitleRow>
            <NavInfo>
              <NavDetail>
                <NavLabel>Menus:</NavLabel> {availMenus.length} available
                {savedFilters && (
                  <>
                    {' '}
                    (
                    {
                      Object.values(savedFilters).filter((f) => f != null)
                        .length
                    }{' '}
                    filters)
                  </>
                )}
              </NavDetail>
            </NavInfo>
          </NavSectionContent>
        </WorkflowSection>

        <NavDivider />

        <WorkflowSection
          active={currentView === PlannerView.MenuSuggestions}
          onClick={() => setCurrentView(PlannerView.MenuSuggestions)}
          disabled={availMenus.length === 0}
        >
          <NavSectionContent>
            <NavTitleRow>
              <NavTitle>2. Map & Build Date Menus</NavTitle>
              <InfoIconWrapper
                onClick={(e) => {
                  e.stopPropagation()
                  handleShowMapInfo()
                }}
              >
                <StyledInfoIcon size={22} />
              </InfoIconWrapper>
            </NavTitleRow>
            <NavInfo>
              {!account || !selectedClientLocation ? (
                <NavDetail>Select a client first</NavDetail>
              ) : availMenus.length === 0 ? (
                <NavDetail>Find available menus first</NavDetail>
              ) : (
                <NavDetail>Ready to build date menus</NavDetail>
              )}
            </NavInfo>
          </NavSectionContent>
        </WorkflowSection>
      </WorkflowContainer>

      <ContentContainer>
        {currentView === PlannerView.MenuFinder && (
          <GroupOrderMenusFinder
            hq={hq}
            client={account}
            clientSettings={selectedClientSetting}
            latitude={selectedClientLocation?.coordinates.latitude}
            longitude={selectedClientLocation?.coordinates.longitude}
            savedFilters={savedFilters}
            saveFilters={saveFilters}
            availMenus={availMenus}
            setAvailMenus={setAvailMenus}
            searchGOMenus={searchGOMenus}
            loadCuisines={loadCuisines}
            displayFailureMessage={displayFailureMessage}
            displayInfoMessage={displayInfoMessage}
            showInfoModal={showInfoModal}
            loadAccounts={loadAccounts}
            searchClientSettings={searchClientSettings}
            onSelectClientLocation={onSelectClientLocation}
            withinBudget={withinBudget}
            onChangeWithinBudget={setWithinBudget}
          />
        )}
        {currentView === PlannerView.MenuSuggestions && (
          <MenuSuggestionsMap
            userId={userId}
            userEmail={userEmail}
            client={account}
            clientSettings={selectedClientSetting}
            withinBudget={withinBudget}
            onChangeWithinBudget={setWithinBudget}
            loadAccounts={loadAccounts}
            searchClientSettings={searchClientSettings}
            onSelectClientLocation={onSelectClientLocation}
            loadChefs={loadChefs}
            displayConfirmationModal={displayConfirmationModal}
            createDateMenuTemplate={createDateMenuTemplate}
            updateDateMenuTemplate={updateDateMenuTemplate}
            searchDateMenuTemplates={searchDateMenuTemplates}
            deleteDateMenuTemplates={deleteDateMenuTemplates}
            hqId={hq}
            menus={availMenus}
            cuisineOptions={cuisineOptions}
            clientLocation={selectedClientLocation || undefined}
            clientName={account?.name}
            displayInfoMessage={displayInfoMessage}
            displayFailureMessage={displayFailureMessage}
            showInfoModal={showInfoModal}
            closeInfoModal={closeInfoModal}
          />
        )}
      </ContentContainer>
    </GOSchedulePlannerContainer>
  )
}

const MapInfo = () => {
  return (
    <InfoContainer>
      <InfoDescription>
        The &quot;Map & Build Date Menus&quot; is a tool for mapping menus and
        building date menus. Date menus can be built of a single client or for a
        market. Date menu suggestions are built by building finding menu
        combinations that match the selected menu templates (ie: chefs, cuisines
        restrictions) and picking the combinations that minimize distance
        traveled.
      </InfoDescription>

      <InfoSection>
        <InfoSectionTitle>How To</InfoSectionTitle>

        <InfoSubsection>
          <InfoSubtitle>Map menus in a market</InfoSubtitle>
          <StyledOrderedList>
            <StyledListItem>
              1. Find menus using the menu search tool
            </StyledListItem>
            <StyledListItem>
              2. Navigate to &quot;Map & Build Date Menus&quot; section
            </StyledListItem>
            <StyledListItem>
              3. Select &quot;All Available Menus&quot; from the display mode
              dropdown
            </StyledListItem>
            <StyledListItem>
              4. Optionally filter chefs by searching by chef name, menu name,
              cuisine
            </StyledListItem>
            <StyledListItem>
              5. Select chef card to center and highlight chef on the map
            </StyledListItem>
          </StyledOrderedList>
        </InfoSubsection>

        <InfoSubsection>
          <InfoSubtitle>Build date menus for a client</InfoSubtitle>
          <StyledOrderedList>
            <StyledListItem>
              1. Navigate to &quot;Menu Search&quot; and select a client and
              location
            </StyledListItem>
            <StyledListItem>
              2. Find menus using the menu search tool
            </StyledListItem>
            <StyledListItem>
              3. Optionally curate the menus you want to consider for this
              client ie: by date if you want to build just a single date menu,
              by cuisine to filter out disliked cuisines, apply recently ordered
              filters, exclude chefs, or pressing X next to individual menus to
              exclude them
            </StyledListItem>
            <StyledListItem>
              4. Navigate to &quot;Map & Build Date Menus&quot; section and
              select &quot;Client Menus&quot; from the display mode dropdown
            </StyledListItem>
            <StyledListItem>
              5. Press &quot;Select Template&quot; which will open a modal where
              you can select an existing Date Menu Template or create / edit
              one. Create or select a template by pressing &quot;Select&quot;
            </StyledListItem>
            <StyledListItem>
              6. Press &quot;Generate Suggestions&quot; which will build a list
              of Date Menus for this client minimizing distance traveled for
              each Date Menu that matches the selected template
            </StyledListItem>
            <StyledListItem>
              7. Click through individual suggestions to review their menus,
              chefs, and routes
            </StyledListItem>
            <StyledListItem>
              7. Press &quot;Export to CSV&quot; to download to your computer
            </StyledListItem>
            <StyledListItem>
              8. Optionally edit the CSV by adjusting combinations for each
              suggestion editing just the columns that say &quot;Editable&quot;
              and then pressing &quot;Import Date MEnus from CSV&quot; to view
              updated routes and total distances for each Date Menu
            </StyledListItem>
          </StyledOrderedList>
        </InfoSubsection>

        <InfoSubsection>
          <InfoSubtitle>Build market menus</InfoSubtitle>
          <StyledOrderedList>
            <StyledListItem>
              1. Navigate to &quot;Menu Search&quot; and search for menus.
              Generally you will want to include all active chefs for this
              analysis.
            </StyledListItem>
            <StyledListItem>
              2. Navigate to &quot;Map & Build Date Menus&quot; section and
              select &quot;Market Menus&quot; from the display mode dropdown
            </StyledListItem>
            <StyledListItem>
              3. Press &quot;Select Template&quot; which will open a modal where
              you can select an existing Date Menu Template or create / edit
              one. Create or select a template by pressing &quot;Select&quot;
            </StyledListItem>
            <StyledListItem>
              4. Use the filter and search options to select 1 or more chefs as
              targets to build market menus around. These will generally be
              anchor chefs who can support large volumes of orders.
            </StyledListItem>
            <StyledListItem>
              5. Press &quot;Generate Suggestions&quot; which will build a list
              of Date Menus for the selected chefs minimizing distance traveled
              for each Date Menu that matches the selected template
            </StyledListItem>
            <StyledListItem>
              6. Each chef will have a number of non overlapping date menus
              suggested. Click through them to review their routes and menu
              choices.
            </StyledListItem>
            <StyledListItem>
              7. Optionally &quot;Export to CSV&quot; to download to your
              computer, edit, and import again to make any adjustments
            </StyledListItem>
          </StyledOrderedList>
        </InfoSubsection>
      </InfoSection>

      <InfoSection>
        <InfoSectionTitle>Features</InfoSectionTitle>
        <FeatureItem>
          <FeatureTitle>Date Menu Templates</FeatureTitle>
          <p>
            These templates are used to provide a structure for building date
            menus.
            <StyledOrderedList>
              <StyledListItem>
                <strong>Scope and Privacy</strong> Template visibility can be
                adjusted to be global, market, or private this way templates can
                be used for specific markets or copied for personal use if a
                user has particular use cases they are handling.
              </StyledListItem>
              <StyledListItem>
                <strong>Menus</strong> Menu templates specify what conditions an
                acceptable menu must meet in order to be considered for this
                date menu. These conditions include chef inclusion / exclusion
                requirements, cuisine inclusion / exclusion requirements, and
                maximum number of reuses. This specifies how many times a menu
                can be reused for this menu slot. This is useful for markets
                with limited Sandwich or Salad options where duplicates are
                permissable..
              </StyledListItem>
              <StyledListItem>
                <strong>Import / Export</strong> Templates can be imported and
                exported as text to your clipboard. This can be used to save
                commonly used templates or share templates with others more
                easily but is generally unnecssary given there is a save
                feature.
              </StyledListItem>
            </StyledOrderedList>
          </p>
        </FeatureItem>
      </InfoSection>
    </InfoContainer>
  )
}

const MenuFinderInfo = () => {
  return (
    <InfoContainer>
      <InfoDescription>
        The &quot;Menu Search&quot; is a tool for finding Group Order menus that
        are available in a market. This can be used for menu analysis or to use
        in the Map & Build Date Menus section.
      </InfoDescription>

      <InfoSection>
        <InfoSectionTitle>How To</InfoSectionTitle>

        <InfoSubsection>
          <InfoSubtitle>Find and export market menus</InfoSubtitle>
          <StyledOrderedList>
            <StyledListItem>
              1. Optionally select a date for &quot;Available On&quot; to only
              return menus available on that date
            </StyledListItem>
            <StyledListItem>
              2. Press &quot;Search&quot; and all the active menus in the market
              will be shown
            </StyledListItem>
            <StyledListItem>
              3. Press &quot;Export to CSV&quot; to download the menus to your
              computer
            </StyledListItem>
          </StyledOrderedList>
        </InfoSubsection>

        <InfoSubsection>
          <InfoSubtitle>Find menus close to a client</InfoSubtitle>
          <StyledOrderedList>
            <StyledListItem>
              1. Select a client and client location
            </StyledListItem>
            <StyledListItem>
              2. Add a value for &quot;Max Distance To Client&quot; (e.g. 15)
              this field is mandatory when a client is selected
            </StyledListItem>
            <StyledListItem>3. Press &quot;Search&quot;</StyledListItem>
          </StyledOrderedList>
        </InfoSubsection>

        <InfoSubsection>
          <InfoSubtitle>
            Find menus client has not ordered in a while
          </InfoSubtitle>
          <StyledOrderedList>
            <StyledListItem>
              1. Select a client and client location
            </StyledListItem>
            <StyledListItem>
              2. Include a large value for &quot;Max Distance To Client&quot;
              (e.g. 100) this field is mandatory when a client is selected
            </StyledListItem>
            <StyledListItem>
              3. Optionaly include a value for &quot;Min Weeks Last
              Scheduled&quot; to exclude menus that have been ordered recently
            </StyledListItem>
            <StyledListItem>4. Press &quot;Search&quot;</StyledListItem>
            <StyledListItem>
              5. Column &quot;Last Ordered by Client&quot; will show the date
              this menu was last ordered at this location
            </StyledListItem>
          </StyledOrderedList>
        </InfoSubsection>
      </InfoSection>

      <InfoSection>
        <InfoSectionTitle>Features</InfoSectionTitle>
        <FeatureItem>
          <FeatureTitle>Duplicate Menus</FeatureTitle>
          <p>
            There are many duplicate menus in our system generally created with
            different pricing or are client specific. These are variations of a
            single menu and should usually be removed when considering menu
            options. Most of these duplicate follow a similar pattern &quot;Name
            - Variation Name&quot;. When the &quot;Exclude client specific
            menus&quot; filter is selected, these duplicate menus will be
            removed and retaining just one of the duplicates. The removed
            duplicates can be viewed by pressing the &quot;X Duplicate Menus
            Excluded&quot; button. For example menus with name
            &quot;Sandwiches&quot; and &quot;Sandwiches - Natera&quot; will
            remove &quot;Sandwiches - Natera&quot; from the list.
          </p>
        </FeatureItem>
        <FeatureItem>
          <FeatureTitle>Export / Import Filters</FeatureTitle>
          <p>
            Filters can be exported as text to your clipboard and saved locally
            so that they can be reused or shared with others. This can be
            helpful in saving commonly used filters you might have or making
            broader adjustments. If necessary we can add support for saving
            filters in the future.
          </p>
        </FeatureItem>
        <FeatureItem>
          <FeatureTitle>Column Visibility</FeatureTitle>
          <p>
            Select which columns to show in the table. This can be helpful in
            reducing the number of columns making it easier to browse the list.
          </p>
        </FeatureItem>
      </InfoSection>
    </InfoContainer>
  )
}

const NavTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`

const InfoIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;

  &:hover {
    transform: scale(1.1);
  }
`

const StyledInfoIcon = styled(FiInfo)`
  color: ${colors.blue400};
`

const InfoContainer = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  line-height: 1.5;
  color: ${colors.dark500};
  max-width: 800px;
  margin: 0 auto;
  padding: 15px 0;
`

const InfoDescription = styled.p`
  font-size: 15px;
  margin-bottom: 24px;
`

const InfoSection = styled.div`
  margin-bottom: 24px;
  margin-top: 10px;
`

const InfoSectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid ${colors.gray200};
  color: ${colors.blue400};
`

const InfoSubsection = styled.div`
  margin-bottom: 16px;
`

const InfoSubtitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: ${colors.dark500};
`

const FeatureItem = styled.div`
  margin-bottom: 16px;

  p {
    margin: 8px 0 0 0;
    font-size: 14px;
  }
`

const FeatureTitle = styled.h4`
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: ${colors.dark500};
`

// Add styled components for numbered lists
const StyledOrderedList = styled.ol`
  margin: 0;
  padding-left: 24px;
  counter-reset: item;
`

const StyledListItem = styled.li`
  margin-bottom: 8px;
  position: relative;
  padding-left: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`

// Styled components
const GOSchedulePlannerContainer = styled.div`
  height: 100%;
  width: 100%;
  min-width: 1600px;
`

const NavContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: ${colors.gray100};
  padding: 10px 15px;
  border-radius: 8px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`

const ClientSelectionSection = styled.div`
  flex: 1;
  padding: 10px 15px;
  background-color: white;
  border-radius: 6px;
  border: 1px solid ${colors.gray200};
`

const NavSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const NavSectionTitle = styled.h2`
  font-size: 16px;
  margin: 0;
  color: ${colors.dark500};
  font-weight: 600;
`

const ClientSelectedInfo = styled.div`
  font-size: 13px;
  color: ${colors.blue400};
  font-weight: 500;
`

const WorkflowContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: ${colors.gray100};
  padding: 10px 15px;
  border-radius: 8px;
  margin-bottom: 15px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`

const WorkflowSection = styled.div<{ active: boolean; disabled?: boolean }>`
  flex: 1;
  padding: 10px 15px;
  background-color: ${(props) => (props.active ? colors.blue100 : 'white')};
  border-radius: 6px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
  transition: all 0.2s ease;
  border: 1px solid
    ${(props) => (props.active ? colors.blue400 : colors.gray200)};

  &:hover {
    background-color: ${(props) =>
      props.disabled
        ? 'white'
        : props.active
          ? colors.blue100
          : colors.gray100};
  }
`

const NavSectionContent = styled.div`
  display: flex;
  flex-direction: column;
`

const NavDivider = styled.div`
  height: 40px;
  width: 1px;
  background-color: ${colors.gray300};
  margin: 0 20px;
`

const NavTitle = styled.h2`
  font-size: 16px;
  margin: 0 0 8px 0;
  color: ${colors.dark500};
  font-weight: 600;
`

const NavInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const NavDetail = styled.div`
  font-size: 13px;
  color: ${colors.dark500};
`

const NavLabel = styled.span`
  font-weight: 500;
`

const ContentContainer = styled.div`
  height: calc(100% - 80px);
  overflow: auto;
`

export default GroupOrderSchedulePlanner
